/**
 * Time-window filter.
 *
 * Two controls: a window bucket (Today / 7d / 30d / All) plus an FSRS
 * "Due today" pill that intersects with today's revision plan. Both
 * combine orthogonally: "Due today" + "Last 7 days" returns concepts
 * scheduled for today that were also active within the past week.
 *
 * The window applies to a single fixed axis per surface - Knowledge
 * Archive uses `last_reviewed_at` (the default in `asGraphFilters`)
 * and Quick Bytes uses `created_at` (set in `asHighlightsFilters`).
 * Exposing an axis switch in the UI added complexity without enough
 * user value, so it lives as a per-surface default now.
 *
 * The window is ONE choice, so it is a segmented control, as the app's other
 * single choices are; it used to be four pills where the unselected ones
 * looked filled and the selected one looked like a link. "Due today" and
 * "Show paused" are not time at all - they are one-tap narrowings - so they
 * sit apart under "Show only": on the desktop panel inside this section, and
 * in the phone sheet as their own section, first, because they are the
 * quickest filter to reach for.
 */

import { useState } from 'react';
import { CalendarCheck, Check, Clock, PauseCircle } from 'lucide-react';
import { SegmentedControl } from '@ai-tutor/ui';
import { useTagFilters } from '@/hooks/mindspace/useTagFilters';
import type { TimeWindow } from '@/hooks/mindspace/useTagFilters';
import { FilterSection } from '@/components/features/mindspace/filters/FilterSection';
import { useFilterLayout } from '@/components/features/mindspace/filters/filterLayout';
import { filterChip } from '@/components/features/mindspace/filters/filterStyles';

const WINDOW_OPTIONS: { value: TimeWindow; label: string }[] = [
  { value: 'all', label: 'All time' },
  { value: 'today', label: 'Today' },
  { value: '7d', label: '7 days' },
  { value: '30d', label: '30 days' },
];

interface TimeWindowFilterProps {
  /** "Show paused" adds paused concepts to the map - only meaningful on the
   *  Knowledge Archive graph, so it's hidden on Quick Bytes by default. */
  showPausedToggle?: boolean;
  /** Section open on first render. Story-only hatch, initial state only — no
   *  production call site passes it, and the panels all mount collapsed. It
   *  exists because the section body (chips, window pills, the lesson list) is
   *  otherwise reachable only by clicking, and a click-only state has no resting
   *  story to diff. Same escape hatch as `FlowSelector`/`SearchInput`'s
   *  `defaultOpen`. */
  defaultOpen?: boolean;
}

export function TimeWindowFilter({
  showPausedToggle = false,
  defaultOpen = false,
}: TimeWindowFilterProps) {
  const { window, dueToday, includeArchived, setFilters } = useTagFilters();
  const [open, setOpen] = useState(defaultOpen);
  const sheet = useFilterLayout() === 'sheet';

  // Only factor "Show paused" into active state when the toggle is rendered,
  // so surfaces that hide it (Quick Bytes) don't show a phantom count.
  const pausedActive = showPausedToggle && includeArchived;
  const togglesOn = (dueToday ? 1 : 0) + (pausedActive ? 1 : 0);
  const windowOn = window !== 'all';

  const clearToggles = () =>
    setFilters({
      dueToday: false,
      ...(showPausedToggle ? { includeArchived: false } : {}),
    });
  const windowPicker = <WindowPicker sheet={sheet} />;
  const toggles = <ShowOnly sheet={sheet} showPaused={showPausedToggle} />;

  if (sheet) {
    return (
      <>
        <FilterSection
          icon={null}
          label='Show only'
          count={togglesOn}
          open
          onOpenChange={() => {}}
          onClear={togglesOn ? clearToggles : undefined}
        >
          {toggles}
        </FilterSection>
        <FilterSection
          icon={null}
          label='Time window'
          open
          onOpenChange={() => {}}
          onClear={windowOn ? () => setFilters({ window: 'all' }) : undefined}
        >
          {windowPicker}
        </FilterSection>
      </>
    );
  }

  return (
    <FilterSection
      icon={<Clock className='h-3.5 w-3.5' />}
      label='Time window'
      count={togglesOn + (windowOn ? 1 : 0)}
      open={open}
      onOpenChange={setOpen}
      onClear={
        togglesOn || windowOn
          ? () =>
              setFilters({
                window: 'all',
                dueToday: false,
                ...(showPausedToggle ? { includeArchived: false } : {}),
              })
          : undefined
      }
    >
      <p className='mb-1.5 text-xs text-muted-foreground'>Window</p>
      {windowPicker}
      <p className='mb-1.5 mt-3 text-xs text-muted-foreground'>Show only</p>
      {toggles}
    </FilterSection>
  );
}

/** All time | Today | 7 days | 30 days - one choice, one control. */
function WindowPicker({ sheet }: { sheet: boolean }) {
  const { window, setFilters } = useTagFilters();
  return (
    <SegmentedControl
      label='Time window'
      options={WINDOW_OPTIONS}
      value={window}
      onChange={value => setFilters({ window: value })}
      size='sm'
      className='flex w-full'
      segmentClassName={sheet ? 'tap-target flex-1 py-1.5 text-sm' : 'flex-1'}
    />
  );
}

/** "Due today" and, on the archive, "Show paused": independent toggles. */
function ShowOnly({
  sheet,
  showPaused,
}: {
  sheet: boolean;
  showPaused: boolean;
}) {
  const { dueToday, includeArchived, setFilters } = useTagFilters();
  const size = sheet ? 'touch' : 'sm';
  return (
    <div
      className={
        sheet ? 'flex flex-wrap gap-x-1.5 gap-y-2' : 'flex flex-wrap gap-1.5'
      }
    >
      <button
        type='button'
        onClick={() => setFilters({ dueToday: !dueToday })}
        aria-pressed={dueToday}
        className={filterChip({ on: dueToday, size })}
      >
        {dueToday ? (
          <Check className='h-3 w-3' aria-hidden='true' />
        ) : (
          <CalendarCheck className='h-3 w-3' aria-hidden='true' />
        )}
        Due today
      </button>
      {showPaused && (
        <button
          type='button'
          onClick={() => setFilters({ includeArchived: !includeArchived })}
          aria-pressed={includeArchived}
          title='Paused concepts are left out of reviews. Turn this on to see them on the map as hollow rings, and resume any of them.'
          className={filterChip({ on: includeArchived, size })}
        >
          {includeArchived ? (
            <Check className='h-3 w-3' aria-hidden='true' />
          ) : (
            <PauseCircle className='h-3 w-3' aria-hidden='true' />
          )}
          Show paused
        </button>
      )}
    </div>
  );
}
